import React from 'react';
import { Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Text } from '../Text/Text';
import { PAGE_SIZE_OPTIONS, type PageSize } from '../../utils/pagination';

export interface PaginationProps {
  page: number;
  totalPages: number;
  pageSize: PageSize;
  totalItems: number;
  /** Defaults to PAGE_SIZE_OPTIONS = [25, 50, 100] */
  pageSizeOptions?: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: PageSize) => void;
}

/**
 * Shared pagination bar used by TicketsPage, AdminDashboard, and any future
 * list page. Renders: result count, rows-per-page picker, and prev/page-numbers/next.
 *
 * Every control is a pill. The active option — page size or page number — is a
 * solid `fg-primary` chip: near-black on the light theme, and its inverse on the
 * dark one, so the selection stays the highest-contrast thing in the bar either way.
 */
export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  pageSize,
  totalItems,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
}) => {
  if (totalItems === 0) return null;

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const pages: (number | '…')[] = [];
  if (totalPages <= 11) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 4) pages.push('…');
    for (let i = Math.max(2, page - 3); i <= Math.min(totalPages - 1, page + 3); i++) pages.push(i);
    if (page < totalPages - 4) pages.push('…');
    pages.push(totalPages);
  }

  /** Solid chip for the active option; quiet text for the rest. */
  const chipClass = (active: boolean) =>
    `rounded-bg-v2-full text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium transition-colors ${
      active
        ? 'bg-fg-primary text-bg-primary'
        : 'text-text-secondary hover:text-text-primary hover:bg-bg-secondary'
    }`;

  /** Outlined pill shared by Prev and Next. */
  const stepClass =
    'flex items-center gap-bg-v2-xs h-9 px-bg-v2-lg rounded-bg-v2-full border border-border-primary bg-bg-primary text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium text-text-secondary hover:bg-bg-secondary hover:text-text-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-bg-primary disabled:hover:text-text-secondary transition-colors';

  return (
    <div className="flex items-center justify-between px-bg-v2-xl py-bg-v2-md border-t border-border-secondary flex-shrink-0 bg-bg-primary gap-bg-v2-lg flex-wrap">

      {/* Left: result count + page-size picker */}
      <div className="flex items-center gap-bg-v2-lg">
        <Text variant="text-sm" weight="regular" color="quaternary">
          {startItem}–{endItem} of {totalItems} results
        </Text>

        <div className="flex items-center gap-bg-v2-md">
          <Text variant="text-sm" weight="medium" color="primary">Rows per page</Text>

          {/* Segmented pill — one bordered track, active size filled solid. */}
          <div className="flex items-center gap-bg-v2-xs p-bg-v2-xxs rounded-bg-v2-full border border-border-primary">
            {pageSizeOptions.map(size => (
              <button
                key={size}
                type="button"
                aria-pressed={pageSize === size}
                onClick={() => { onPageSizeChange(size as PageSize); onPageChange(1); }}
                className={`min-w-[44px] h-8 px-bg-v2-lg ${chipClass(pageSize === size)}`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: prev / page numbers / next */}
      <div className="flex items-center gap-bg-v2-xs">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={stepClass}
        >
          <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
          <span>Prev</span>
        </button>

        <div className="flex items-center gap-bg-v2-xs">
          {pages.map((p, i) =>
            p === '…' ? (
              <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-bg-v2-sm text-text-quaternary select-none">…</span>
            ) : (
              <button
                key={p}
                type="button"
                aria-current={page === p ? 'page' : undefined}
                onClick={() => onPageChange(p as number)}
                className={`w-9 h-9 ${chipClass(page === p)}`}
              >
                {p}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className={stepClass}
        >
          <span>Next</span>
          <Icon name={IconName.ArrowRight} size="sm" inheritColor />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
